// Imports
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import formatForDatetimeLocalInput from '../../../../../../shareables/front-end/utils/format-for-datetime-local-input';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import LoadingRow from '../../../../../../shareables/foundation/front-end/components/tables/loading-row';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { PostGarageSaleParameters } from '../../../../../../shareables/types/api/main/website/page/type/212/garage-sale/post';
import { GetGarageSalesResponse } from '../../../../../../shareables/types/api/main/website/page/type/212/garage-sales/get';
import Panel from '../shared-components/panel';
import Table from '../shared-components/table';
import EmptyStateRow from '../shared-components/table/empty-state-row';
import UserControlButton from '../shared-components/user-control-button';
import GarageSalesRow from './row';


// Define the accepted props
interface Props {
	pageID: number;
	storybookGarageSales?: GetGarageSalesResponse;
	storybookIsPanelOpen?: true;
}


// Function component
const GarageSales: React.FC<Props> = ({ pageID, storybookGarageSales, storybookIsPanelOpen }) => {
	// Use state
	const [garageSales, setGarageSales] = useState<GetGarageSalesResponse | null>(storybookGarageSales ?? null);
	const [isPanelOpen, setIsPanelOpen] = useState(storybookIsPanelOpen ?? false);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use ref
	const inputSetters = useRef<((value: string) => void)[]>([]);
	
	
	// Function that loads garage sales
	const memoizedLoadGarageSales = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetGarageSalesResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/212/garage-sales`,
				completion: (json) => {
					setGarageSales(json.response);
				},
			})
		);
	}, [dispatch, pageID]);
	
	
	// Load garage sales on mount
	useEffect(() => {
		memoizedLoadGarageSales();
	}, [memoizedLoadGarageSales]);
	
	
	// Build form options
	const formOptions: APIFormOptions<PostGarageSaleParameters, undefined, undefined> = {
		action: 'POST',
		uri: `/website/${window.location.hostname}/page/id_${pageID}/type/212/garage-sale`,
		submitButton: {
			text: 'Post',
		},
	};
	
	
	// Function that handles closing the panel and resetting the form
	const closePanelAndResetForm = () => {
		// Close panel
		setIsPanelOpen(false);
		
		
		// Reset form inputs
		if (inputSetters.current.length > 0) {
			for (const inputSetter of inputSetters.current) {
				inputSetter('');
			}
		}
	};
	
	
	// Handle form completion
	const handleFormCompletion = () => {
		// Display page message
		dispatch(
			commonActions.showPageMessage({
				title: 'Success!',
				message: 'Your garage sale has been posted.',
				color: 'success',
			})
		);
		
		
		// Reload garage sales
		memoizedLoadGarageSales();
		
		
		// Close panel and reset form
		closePanelAndResetForm();
	};
	
	
	// Determine if user can remove at least one garage sale
	const displayRemoveColumn = Boolean(garageSales?.some((garageSale) => garageSale.canDelete));
	
	
	// Return JSX
	return (
		<React.Fragment>
			<UserControlButton
				text='Add your garage sale'
				opened={isPanelOpen}
				onClick={() => setIsPanelOpen(true)}
				icon={faPlus}
			/>
			
			<AnimateHeight height={isPanelOpen ? 'auto' : 0} duration={400} animateOpacity>
				<Panel closeOnClick={closePanelAndResetForm} signInText='posting a garage sale'>
					<APIForm options={formOptions} onCancel={() => closePanelAndResetForm()} onCompletion={handleFormCompletion}>
						<Input
							type='datetime-local'
							name='start'
							label='Start date/time'
							required
							min={formatForDatetimeLocalInput(new Date(Date.now()))}
							outputRaw
							bindSetter={(setter) => inputSetters.current.push(setter)}
						/>
						<Input
							type='text'
							name='address'
							label='Address'
							maxLength={100}
							placeholder='1234 Maple Street'
							required
							bindSetter={(setter) => inputSetters.current.push(setter)}
						/>
						<Input
							type='text'
							name='host'
							label='Who’s hosting this sale?'
							maxLength={75}
							required
							bindSetter={(setter) => inputSetters.current.push(setter)}
						/>
					</APIForm>
				</Panel>
			</AnimateHeight>
			
			<Table>
				<thead>
					<tr>
						<th>When</th>
						<th>Where</th>
						<th>Who</th>
						{displayRemoveColumn && <th style={{ textAlign: 'center' }}>Remove</th>}
					</tr>
				</thead>
				
				<tbody>
					{garageSales?.map((garageSale) => (
						<GarageSalesRow
							key={garageSale.id}
							garageSale={garageSale}
							pageID={pageID}
							onChange={memoizedLoadGarageSales}
							displayRemoveCell={displayRemoveColumn}
						/>
					))}
					
					{garageSales?.length === 0 && <EmptyStateRow>No upcoming garage sales…</EmptyStateRow>}
					
					{!garageSales && <LoadingRow />}
				</tbody>
			</Table>
		</React.Fragment>
	);
};

export default GarageSales;
